import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { add, differenceInMinutes, differenceInSeconds, isBefore, max, min, sub } from 'date-fns';
import { uniqBy } from 'remeda';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { RouterStateService } from 'src/app/root/router-state.service';
import { formatDateStandard } from 'src/app/shared/standard-date.pipe';
import { InternalMappingData, ShiftOrderDetails, ShiftService, Waypoint } from '../../internal/shift.service';
import { LOAD_ID, SHIFT_ID } from '../loads-routing.constants';

interface Box {
  name: string;
  startPercent: number;
  height: number;
}

interface BoxOrder extends Box {
  order: ShiftOrderDetails;
}

interface ActiveWaypoint {
  locationPercent: number;
  actualTime: string;
  waypoint: Waypoint;
}

interface ShiftEvent {
  type: 'dispatch' | 'sms' | 'hosEdit' | 'checkin' | 'archived' | 'hazardReported' | 'hazardLiked' | 'pause';
  iconName: string;
  iconClass: string;
  time: string;
  message: string;
}

interface ShiftEvents {
  locationPercent: number;
  shiftEvents: ShiftEvent[];
  rightPx: number;
  icons: { name: string; className: string }[];
}

interface HourMarker {
  locationPercent: number;
  time: string;
}

@Component({
  selector: 'lohi-public-shift-timeline',
  templateUrl: './public-shift-timeline.component.html',
  styles: [],
})
export class PublicShiftTimelineComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  private mapInfo: InternalMappingData;
  public beforeBox: Box;
  public shiftBox: Box;
  public afterBox: Box;
  public orderBoxes: BoxOrder[];
  public activeWaypoint: ActiveWaypoint;
  public hourMarkers: HourMarker[];
  public dateWaypointCache: Record<string, Waypoint> = {};
  public shiftEvents: ShiftEvents[];
  public selectedOrderId: number;

  private percentFn: (date: string) => number;
  private percentToTime: (percent: number) => string;

  constructor(
    private shiftService: ShiftService,
    private cd: ChangeDetectorRef,
    private routerState: RouterStateService,
  ) {}

  public ngOnInit(): void {
    this.shiftService.mapInfo$.pipe(takeUntil(this.destroy$$)).subscribe((mapInfo) => {
      this.mapInfo = mapInfo;
      this.calculate();
    });
    this.checkForLoadParam();
    this.checkForShiftParam();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public async checkForShiftParam() {
    const shiftId = await this.routerState.listenForParamChange$(SHIFT_ID).pipe(take(1)).toPromise();
    if (shiftId && !!+shiftId) {
      this.loadShiftByShiftId(+shiftId);
    }
  }

  public loadShiftByShiftId(shiftId: number) {
    if (!shiftId) {
      return;
    }
    this.clearAll();
    this.shiftService.loadMapInfoByShift(shiftId);
  }

  public async checkForLoadParam() {
    const orderId = await this.routerState.listenForParamChange$(LOAD_ID).pipe(take(1)).toPromise();
    if (orderId) {
      this.loadShiftByOrder(orderId);
    }
  }

  public loadShiftByOrder(orderId: string) {
    if (!orderId) {
      return;
    }
    this.clearAll();
    this.shiftService.loadMapInfoByOrder(orderId);
  }

  public highlightWaypoint(event: MouseEvent) {
    if (!this.mapInfo) {
      return;
    }
    let target = event.target as HTMLElement;
    while (target && target.id !== 'mouseMove') {
      if (target.tagName === 'BODY') {
        return;
      }
      target = target.parentElement;
    }
    const rect = target.getBoundingClientRect();
    const y = event.clientY - rect.top;
    if (y > 0) {
      const locationPercent = (y / rect.height) * 100;
      const time = this.percentToTime(locationPercent);
      const waypoint = this.getWaypointFromDateStr(time);
      if (!waypoint) {
        return;
      }
      this.activeWaypoint = {
        locationPercent,
        actualTime: time,
        waypoint,
      };
      this.cd.markForCheck();
    }
  }

  public highlightOrder(event: Event, orderId: number) {
    event.stopPropagation();
    if (this.selectedOrderId !== orderId) {
      this.selectedOrderId = orderId;
      this.cd.markForCheck();
    }
  }

  private calculate(): void {
    if (!this.mapInfo) {
      this.cd.markForCheck();
      return;
    }
    this.setupPercentFn();
    this.setupBeforeBox();
    this.setupShiftBox();
    this.setupAfterBox();
    this.setupOrders();
    this.setupShiftEvents();
    this.setupHourMarkers();
    this.cd.markForCheck();
  }

  private clearAll() {
    this.mapInfo = null;
    this.beforeBox = null;
    this.shiftBox = null;
    this.afterBox = null;
    this.orderBoxes = null;
    this.activeWaypoint = null;
    this.dateWaypointCache = {};
    this.shiftEvents = null;
    this.hourMarkers = null;
    this.cd.markForCheck();
  }

  private startDate(): Date {
    if (!this.mapInfo) {
      return;
    }
    const sixHoursBeforeShiftStart = sub(new Date(this.mapInfo.shiftInfo.startedAt), { hours: 6 });
    if (!this.mapInfo.waypoints || !this.mapInfo.waypoints.length) {
      return sixHoursBeforeShiftStart;
    }
    const firstWaypoint = this.mapInfo.waypoints[0];
    const firstDate = new Date(firstWaypoint.timestamp);
    return min([firstDate, sixHoursBeforeShiftStart]);
  }

  private endDate(): Date {
    if (!this.mapInfo) {
      return;
    }
    const shiftEnds = [new Date(this.mapInfo.shiftInfo.endedAt)];
    if (this.mapInfo.shiftInfo.archivedAt) {
      shiftEnds.push(new Date(this.mapInfo.shiftInfo.archivedAt));
    }
    const actualShiftEnd = min(shiftEnds);
    const sixHoursAfterShiftEnd = add(new Date(actualShiftEnd), { hours: 6 });
    if (!this.mapInfo.waypoints || !this.mapInfo.waypoints.length) {
      return sixHoursAfterShiftEnd;
    }
    const lastWaypoint = this.mapInfo.waypoints[this.mapInfo.waypoints.length - 1];
    const lastDate = new Date(lastWaypoint.timestamp);
    return max([lastDate, sixHoursAfterShiftEnd]);
  }

  private setupPercentFn() {
    const totalSeconds = differenceInSeconds(this.endDate(), this.startDate());
    this.percentFn = (dateStr: string) => {
      const date = new Date(dateStr);
      const secondsSinceStart = differenceInSeconds(date, this.startDate());
      return (secondsSinceStart / totalSeconds) * 100;
    };
    this.percentToTime = (percent: number): string => {
      const seconds = (percent / 100) * totalSeconds;
      const date = add(this.startDate(), { seconds });
      date.setMilliseconds(0); // Should make caching easier
      return date.toISOString();
    };
  }

  private setupBeforeBox() {
    const startPercent = 0;
    const height = this.percentFn(this.mapInfo.shiftInfo.startedAt);
    this.beforeBox = {
      name: 'Before Shift',
      startPercent,
      height,
    };
  }

  private setupShiftBox() {
    const startPercent = this.percentFn(this.mapInfo.shiftInfo.startedAt);
    const shiftEnds = [new Date(this.mapInfo.shiftInfo.endedAt)];
    if (this.mapInfo.shiftInfo.archivedAt) {
      shiftEnds.push(new Date(this.mapInfo.shiftInfo.archivedAt));
    }
    const actualShiftEnd = min(shiftEnds);
    const height = this.percentFn(actualShiftEnd.toISOString()) - startPercent;
    this.shiftBox = {
      name: `Shift ${this.mapInfo.shiftInfo.id} - ${this.mapInfo.shiftInfo.driverName}`,
      startPercent,
      height,
    };
  }

  private setupAfterBox() {
    const shiftEnds = [new Date(this.mapInfo.shiftInfo.endedAt)];
    if (this.mapInfo.shiftInfo.archivedAt) {
      shiftEnds.push(new Date(this.mapInfo.shiftInfo.archivedAt));
    }
    const actualShiftEnd = min(shiftEnds);
    const startPercent = this.percentFn(actualShiftEnd.toISOString());
    const height = 100 - startPercent;
    this.afterBox = {
      name: `After Shift`,
      startPercent,
      height,
    };
  }

  private setupOrders() {
    this.orderBoxes = (this.mapInfo.orders || []).map((order) => {
      const startPercent = this.percentFn(order.acceptedAt);
      const height = this.percentFn(order.endedAt) - startPercent;
      return {
        name: `${order.id}`,
        startPercent,
        height,
        order,
      };
    });
  }

  private setupShiftEvents() {
    const dispatchEvents: ShiftEvent[] = (this.mapInfo.dispatchEvents || []).map((event) => {
      return {
        type: 'dispatch',
        time: event.createdAt,
        iconName: 'add_to_home_screen',
        iconClass: 'text-blue-500',
        message: `Dispatched ${event.orderId}`,
      };
    });
    const smsEventws: ShiftEvent[] = (this.mapInfo.smsHistory || []).map((sms) => {
      return {
        type: 'sms',
        time: sms.sentAt,
        iconName: 'sms',
        iconClass: 'text-gray-600',
        message: sms.message,
      };
    });
    const hosEditEvents: ShiftEvent[] = (this.mapInfo.hosEdits || []).map((edit) => {
      return {
        type: 'hosEdit',
        time: edit.createdAt,
        iconName: 'update',
        iconClass: 'text-green-500',
        message: `End Date changed from ${formatDateStandard(edit.originallyEndedAt)} to ${formatDateStandard(
          edit.nowEndsAt,
        )}`,
      };
    });
    const hazardReportedEvents: ShiftEvent[] = (this.mapInfo.hazardsReported || []).map((hazard) => {
      return {
        type: 'hazardReported',
        time: hazard.reportedAt,
        iconName: 'warning',
        iconClass: 'text-red-500',
        message: `Reported a ${hazard.type.toUpperCase()} Hazard which received ${hazard.likeCount} likes`,
      };
    });
    const hazardLikedEvents: ShiftEvent[] = (this.mapInfo.hazardsLiked || []).map((hazard) => {
      return {
        type: 'hazardLiked',
        time: hazard.likedAt,
        iconName: 'thumb_up',
        iconClass: 'text-blue-500',
        message: `Liked a hazard of type ${hazard.type.toUpperCase()}`,
      };
    });
    const pauseEvents: ShiftEvent[] = (this.mapInfo.pauses || []).map((pause) => {
      return {
        type: 'pause',
        time: pause.createdAt,
        iconName: 'pause_circle',
        iconClass: 'text-gray-700',
        message: `Paused Dispatching for ${
          differenceInMinutes(new Date(pause.endTimestamp), new Date(pause.createdAt)) + 1
        }m (${pause.isEnded ? 'ended' : 'ongoing'})`,
      };
    });
    const shiftEvents = [
      ...dispatchEvents,
      ...smsEventws,
      ...hosEditEvents,
      ...hazardReportedEvents,
      ...hazardLikedEvents,
      ...pauseEvents,
    ];
    if (this.mapInfo.shiftInfo.checkedInAt) {
      shiftEvents.push({
        type: 'checkin',
        time: this.mapInfo.shiftInfo.checkedInAt,
        iconName: 'where_to_vote',
        iconClass: 'text-yellow-500',
        message: `${this.mapInfo.shiftInfo.driverName} checked into shift`,
      });
    }
    if (this.mapInfo.shiftInfo.archivedAt) {
      shiftEvents.push({
        type: 'archived',
        time: this.mapInfo.shiftInfo.archivedAt,
        iconName: 'auto_delete',
        iconClass: 'text-red-500',
        message: `Shift was archived`,
      });
    }
    // sort icons
    shiftEvents.sort((a, b) => a.time.localeCompare(b.time));
    // bucket icons
    let shiftEventsGrouped: ShiftEvents[] = [];
    if (shiftEvents.length) {
      shiftEventsGrouped.push({
        locationPercent: this.percentFn(shiftEvents[0].time),
        shiftEvents: [shiftEvents[0]],
        rightPx: 0,
        icons: [],
      });
      shiftEventsGrouped = shiftEvents.reduce((acc, e, index) => {
        if (index === 0) {
          return acc;
        }
        const lastGroup = acc[acc.length - 1];
        const lastEvent = lastGroup.shiftEvents[lastGroup.shiftEvents.length - 1];
        const lastEventDate = new Date(lastEvent.time);
        const thisEventDate = new Date(e.time);
        if (differenceInMinutes(thisEventDate, lastEventDate) < 15 && lastGroup.shiftEvents.length < 6) {
          lastGroup.shiftEvents.push(e);
        } else {
          acc.push({
            locationPercent: this.percentFn(e.time),
            shiftEvents: [e],
            rightPx: 0,
            icons: [],
          });
        }
        return acc;
      }, shiftEventsGrouped);

      shiftEventsGrouped.forEach((group, index) => {
        const px = (4 - (index % 4)) * 40; // 200 px, 32px width icons so can fit 4 with some padding on the left an
        group.rightPx = px;
        group.icons = group.shiftEvents.map((e) => ({ name: e.iconName, className: e.iconClass }));
        group.icons = uniqBy(group.icons, (g) => g.name);
        if (group.icons.length > 4) {
          group.icons.length = 3;
          group.icons.push({
            name: 'read_more',
            className: 'text-black',
          });
        }
      });
    }

    this.shiftEvents = shiftEventsGrouped;
  }

  private setupHourMarkers() {
    let markerDate = this.startDate();
    const endDate = sub(this.endDate(), { hours: 1 });
    markerDate.setMinutes(0, 0, 0);
    markerDate = add(markerDate, { hours: 1 });
    const markers: HourMarker[] = [];
    while (isBefore(markerDate, endDate)) {
      const iso = markerDate.toISOString();
      markers.push({
        locationPercent: this.percentFn(iso),
        time: iso,
      });
      markerDate = add(markerDate, { hours: 1 });
    }
    this.hourMarkers = markers;
  }

  private getWaypointFromDateStr(dateStr: string): Waypoint {
    if (!this.dateWaypointCache[dateStr]) {
      const waypoint = waypointSearch(this.mapInfo.waypoints, dateStr);
      if (waypoint) {
        this.dateWaypointCache[dateStr] = waypoint;
      }
    }
    return this.dateWaypointCache[dateStr];
  }
}

function waypointSearch(waypoints: Waypoint[], targetTime: string): Waypoint {
  if (!waypoints?.length) {
    return null;
  }
  const midpoint = Math.floor(waypoints.length / 2);

  if (waypoints[midpoint].timestamp === targetTime) {
    return waypoints[midpoint];
  }
  if (waypoints.length === 1) {
    return waypoints[0];
  }

  if (waypoints[midpoint].timestamp > targetTime) {
    return waypointSearch(waypoints.slice(0, midpoint), targetTime);
  } else if (waypoints[midpoint].timestamp < targetTime) {
    return waypointSearch(waypoints.slice(midpoint), targetTime);
  }
}
